import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap, catchError, throwError, take } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { PageRoutes } from '../constants/pages-routes';
import { Router } from '@angular/router';
import { ApiRoutes } from '../constants/api.routes';
import { UserPermissionInitializerService } from '../services/user-permission-initializer.service';
import { ActiveOrganisationService } from '../services/activeOrganisation.service';
import { UserService } from '../services/users.service';
import { UserRole } from '../constants/userRole';
import { environment } from '../../../environments/environment';
import { AuthLogoutService } from '../services/auth-logout.service';

// Add your public URLs here that don't require authentication
const PUBLIC_URLS = [
    ApiRoutes.GET_INVITE_USER_DETAILS_BY_TOKEN,
    ApiRoutes.VERIFY_INVITED_USER,
    ApiRoutes.GET_ORGANZATION_BY_TOKEN,
    ApiRoutes.RENEW_ORGANIZATION,
    ApiRoutes.SEARCH_ADDRESS,
    ApiRoutes.GET_ADDRESS_BY_ADDRESS_ID,
    ApiRoutes.SEARCH_BUSINESS_DETAILS,
    ApiRoutes.BUSINESS_DETAILS_BY_BUSINESS_NUMBER,
    ApiRoutes.ORGANIZATION_SIGNUP,
    ApiRoutes.VALIDATE_ORGANIZATION
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const permissionService = inject(UserPermissionInitializerService);
    const organizationContext = inject(ActiveOrganisationService);
    const userService = inject(UserService);
    const logoutService = inject(AuthLogoutService);

    // Check if the request is to a public URL
    const isPublic = PUBLIC_URLS.some(url => req.url.includes(url));

    // If request is public, just continue without token
    if (isPublic) {
        return next(req);
    }

    // For protected routes, attach authentication token
    return auth.isAuthenticated$.pipe(
        take(1),
        switchMap(isAuth => {
            if (!isAuth) {
                // Not logged in but accessing protected API
                localStorage.setItem('account_inactive', 'false');
                permissionService.resetPermissions();
                router.navigate([PageRoutes.UNAUTHORIZED]);
                return throwError(() => ({ message: 'Unauthorized' }));
            }

            return auth.getAccessTokenSilently().pipe(
                switchMap(token => {
                    const headers: Record<string, string> = {
                        Authorization: `Bearer ${token}`
                    };
                    const encryptedOrgId = organizationContext.getEncryptedOrganisationId();
                    if (encryptedOrgId && req.url.startsWith(environment.baseApiUrl)) {
                        headers['X-Organisation-Id'] = encryptedOrgId;
                    }
                    const authReq = req.clone({
                        setHeaders: headers
                    });
                    return next(authReq);
                }),
                catchError(error => {
                    if (
                        error?.error === 'login_required' ||
                        error?.error === 'invalid_grant' ||
                        error?.error === 'missing_refresh_token'
                    ) {
                        logoutService.logout();
                    }
                    return throwError(() => error);
                })
            );
        }),
        catchError(error => {
            if (error.status === 401 || error.status === 403) {
                localStorage.setItem('account_inactive', 'false');
                permissionService.resetPermissions();
                router.navigate([PageRoutes.UNAUTHORIZED]);
            }
            //Rights wise access denied - recover exporter default org only on logged-in user details fetch
            if (error.status === 417) {
                const userDetails = userService.loggedInUserDetails();
                if (req.url.includes(ApiRoutes.GET_LOGGED_IN_USER_DETAILS)) {
                    const defaultOrgId = userDetails?.associatedOrganisations?.find((org: any) => org?.isDefault)?.organisationId;
                    if (defaultOrgId && userDetails?.userTypeId == UserRole.EXPORTER) {
                        organizationContext.setActiveOrganisationId(defaultOrgId).then(() => {
                            window.location.reload();
                        });
                    } else {
                        organizationContext.clear();
                        router.navigate([PageRoutes.PAGE + '/' + PageRoutes.DASHBOARD]);
                    }
                } else {
                    router.navigate([PageRoutes.PAGE + '/' + PageRoutes.PAGE_NOT_FOUND]);
                }
            }
            return throwError(() => error);
        })
    );
};
